import React, { useCallback, useEffect, useRef, useState } from 'react';

import { message } from 'antd';
import { T } from 'react-translator-component';

import {
  verifyTwoFactorCode,
  resendTwoFactorCode,
} from '../../../../services/authentication';

const CODE_EXPIRY_SECONDS = 300;
const RESEND_COOLDOWN_SECONDS = 120;

interface UseTwoFactorProps {
  email: string;
  maskedEmail: string;
  onSuccess: (user: any) => void;
  onBackToLogin: () => void;
}

export const useTwoFactor = ({
  email,
  maskedEmail,
  onSuccess,
  onBackToLogin,
}: UseTwoFactorProps) => {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expiryCountdown, setExpiryCountdown] = useState(CODE_EXPIRY_SECONDS);
  const [resendCountdown, setResendCountdown] = useState(
    RESEND_COOLDOWN_SECONDS,
  );
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const [isBlocked, setIsBlocked] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const timer = setInterval(() => {
      setExpiryCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const resetDigits = useCallback(() => {
    setDigits(['', '', '', '', '', '']);
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  }, []);

  const handleVerify = useCallback(
    async (code: string) => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const result = await verifyTwoFactorCode({ email, code });

        if (result.type === 'success' && result.data?.token) {
          onSuccess(result.data.user);
        } else if (result.status === 403) {
          setIsBlocked(true);
          setErrorMessage(
            result.data?.error ||
              T('Account temporarily blocked. Try again in 15 minutes.'),
          );
        } else if (result.status === 401) {
          const remaining = result.data?.attemptsRemaining;
          if (remaining != null) {
            setAttemptsRemaining(remaining);
          }
          setErrorMessage(T('Invalid code. Please try again.'));
          resetDigits();
        } else {
          setErrorMessage(T('Error verifying code. Please try again.'));
          resetDigits();
        }
      } catch {
        setErrorMessage(T('Connection error. Please try again.'));
        resetDigits();
      } finally {
        setIsLoading(false);
      }
    },
    [email, onSuccess, resetDigits],
  );

  const handleDigitChange = useCallback(
    (index: number, value: string) => {
      if (!/^\d*$/.test(value)) return;

      const newDigits = [...digits];
      newDigits[index] = value.slice(-1);
      setDigits(newDigits);

      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }

      if (value && index === 5) {
        const code = newDigits.join('');
        if (code.length === 6) {
          handleVerify(code);
        }
      }
    },
    [digits, handleVerify],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [digits],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 6);
      if (pasted.length === 6) {
        const newDigits = pasted.split('');
        setDigits(newDigits);
        inputRefs.current[5]?.focus();
        handleVerify(pasted);
      }
    },
    [handleVerify],
  );

  const handleResend = useCallback(async () => {
    if (resendCountdown > 0) return;

    setIsLoading(true);
    setErrorMessage('');

    try {
      const result = await resendTwoFactorCode({ email });

      if (result.type === 'success') {
        setResendCountdown(RESEND_COOLDOWN_SECONDS);
        setExpiryCountdown(CODE_EXPIRY_SECONDS);
        setAttemptsRemaining(5);
        resetDigits();
        message.success(T('New code sent to your email.'));
      } else if (result.status === 403) {
        setIsBlocked(true);
        setErrorMessage(
          result.data?.error ||
            T('Account temporarily blocked. Try again in 15 minutes.'),
        );
      } else if (result.data?.retryAfterSeconds) {
        setResendCountdown(result.data.retryAfterSeconds);
        setErrorMessage(T('Please wait before requesting a new code.'));
      } else {
        setErrorMessage(T('Error sending code. Please try again.'));
      }
    } catch {
      setErrorMessage(T('Connection error. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  }, [email, resendCountdown, resetDigits]);

  const formatTime = (seconds: number): string => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  return {
    digits,
    inputRefs,
    isLoading,
    errorMessage,
    expiryCountdown,
    resendCountdown,
    attemptsRemaining,
    isBlocked,
    maskedEmail,
    handleDigitChange,
    handleKeyDown,
    handlePaste,
    handleResend,
    handleBackToLogin: onBackToLogin,
    formatTime,
  };
};
